import React, { useEffect, useState } from 'react';
import { Box, Tabs, Tab, Badge, Button } from '@mui/material';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectEffectiveModules } from '../redux/slices/authSlice';
import { hasModule } from '../data/serverModules';
import { PRIMARY, PRIMARY_LIGHT } from '../theme';
import { TabActionsContext } from '../utils/crumbActions';
import { card, tonalBtn, ghostBtn } from './uiKit';

const PRIMARY_BORDER = '#C9BEFB';

// A tab marked `pageBtn: true` is reached from a button at the right end of the breadcrumb row
// instead of from the tab strip. While one of those pages is open the strip is hidden and its
// button takes you back to the first tab.
export default function TabbedPage({ tabs }) {
    const [params, setParams] = useSearchParams();
    const modules = useSelector(selectEffectiveModules);
    const { setCrumbActions } = useOutletContext() || {};
    const [childActions, setChildActions] = useState(null);

    // A tab's `module` is one access key, or a list of keys where any one is enough.
    const allowedTabs = tabs.filter((t) => !t.module || [].concat(t.module).some((m) => hasModule(modules, m)));
    const allowed = allowedTabs.length ? allowedTabs : tabs.slice(0, 1);
    const stripTabs = allowed.filter((t) => !t.pageBtn);
    const visibleTabs = stripTabs.length ? stripTabs : allowed;
    const pageBtns = allowed.filter((t) => t.pageBtn);

    const requested = params.get('tab');
    const activeKey = allowed.some((t) => t.key === requested) ? requested : visibleTabs[0].key;
    const activeTab = allowed.find((t) => t.key === activeKey);
    const index = visibleTabs.findIndex((t) => t.key === activeKey); // -1 while a page-button page is open

    const select = (_, i) => setParams({ tab: visibleTabs[i].key }, { replace: true });

    const btnKeys = pageBtns.map((t) => t.key).join();
    useEffect(() => {
        if (!setCrumbActions || (!pageBtns.length && !childActions)) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {childActions}
                {pageBtns.map((t) => {
                    const on = activeKey === t.key;
                    return (
                        <Button
                            key={t.key} startIcon={<t.icon sx={{ color: PRIMARY }} />}
                            onClick={() => setParams({ tab: on ? visibleTabs[0].key : t.key }, { replace: true })}
                            sx={on
                                ? { ...tonalBtn, height: 42, px: 2, border: `1px solid ${PRIMARY}` }
                                : { ...ghostBtn, height: 42, px: 2, color: '#334155', '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY_BORDER, color: PRIMARY } }}
                        >
                            {t.label}
                        </Button>
                    );
                })}
            </Box>,
        );
        return () => setCrumbActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, childActions, activeKey, btnKeys]);

    // A tab's own breadcrumb controls are dropped when you switch to another tab.
    useEffect(() => () => setChildActions(null), [activeKey]);

    return (
        <TabActionsContext.Provider value={setChildActions}>
            <Box>
                {!activeTab?.pageBtn && (
                    <Box sx={{ px: 2, pt: 2 }}>
                        <Box sx={{ ...card, px: 1 }}>
                            <Tabs
                                value={index}
                                onChange={select}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    minHeight: 52,
                                    '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: PRIMARY },
                                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: 13.5, color: '#64748B', minHeight: 52, px: 2 },
                                    '& .Mui-selected': { color: `${PRIMARY} !important` },
                                }}
                            >
                                {visibleTabs.map((t) => (
                                    <Tab
                                        key={t.key}
                                        iconPosition="start"
                                        label={t.label}
                                        icon={
                                            t.badge > 0 ? (
                                                <Badge
                                                    badgeContent={t.badge}
                                                    max={99}
                                                    sx={{ '& .MuiBadge-badge': { bgcolor: '#E11D48', color: '#fff', fontSize: 9.5, fontWeight: 800, minWidth: 16, height: 16 } }}
                                                >
                                                    <t.icon sx={{ fontSize: 18 }} />
                                                </Badge>
                                            ) : (
                                                <t.icon sx={{ fontSize: 18 }} />
                                            )
                                        }
                                    />
                                ))}
                            </Tabs>
                        </Box>
                    </Box>
                )}

                {activeTab.render()}
            </Box>
        </TabActionsContext.Provider>
    );
}
